import { Button } from "@sellar-io/components";
import {
  BuyerProductGetResponseChildType,
  BuyerProductGetResponseType,
  StockListResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import { twMerge } from "tailwind-merge";
import { useShallow } from "zustand/react/shallow";
import { ORDER_BUYER } from "../../../../constants/routes";
import { useSellarUser } from "../../../../hooks";
import { useSellarCurrentBusiness } from "../../../../hooks/useSellarCurrentBusiness.shared";
import { useSignupContext } from "../../../../hooks/useSignupContext.shared";
import { useNavigateToChild } from "../../../../hooks/useParentNavigation";
import { useStorefrontFilterStore } from "../../../stockList/StorefrontFilterStore";
import {
  genericContainerTypes,
  GenericContainerTypes,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";

export const SharedButtonContent = ({
  source,
  supplier,
  product,
  connection,
}: {
  source: "market" | "order";
  product:
    | StockListResponseType[number]
    | BuyerProductGetResponseType
    | BuyerProductGetResponseChildType;
  supplier: BuyerProductGetResponseType["Supplier"];
  connection:
    | SupplierStorefrontV2GetResponseType["SupplierRetailerConnection"]
    | null
    | undefined;
}) => {
  const navigateToChild = useNavigateToChild();
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();

  const discoveryState = useDiscoveryV2SearchStore();
  const {
    setFiltersAppliedFromMarket,
    setDietaryType,
    setAbvType,
    setSelectedTags,
  } = useStorefrontFilterStore(
    useShallow((state) => ({
      setFiltersAppliedFromMarket: state.setFiltersAppliedFromMarket,
      setDietaryType: state.setDietaryType,
      setAbvType: state.setAbvType,
      setSelectedTags: state.setSelectedTags,
    })),
    supplier.urlId,
  );

  const { setSignupDialogActive } = useSignupContext();
  const supplierName = supplier.Business.name;
  const redirectProductId = product.ParentId ? product.ParentId : product.id;

  const setPreDefinedStorefrontFilterFromMarket = () => {
    // Sync discovery state to storefront state
    const hasDietaryFilterApplied =
      discoveryState.selectedDietaryTypes.length > 0;
    if (hasDietaryFilterApplied) {
      setDietaryType(discoveryState.selectedDietaryTypes);
    }

    const hasAbvFilterApplied = discoveryState.selectedAbvTypes.length > 0;
    if (hasAbvFilterApplied) {
      setAbvType(discoveryState.selectedAbvTypes);
    }

    const hasContainerFilterApplied =
      discoveryState.selectedAbvTypes.length > 0;
    if (hasContainerFilterApplied) {
      // Filter to only include generic container types
      const genericContainers = discoveryState.selectedContainerTypes.filter(
        (containerType): containerType is GenericContainerTypes =>
          genericContainerTypes.includes(containerType),
      );

      if (genericContainers.length > 0) {
        setSelectedTags(genericContainers);
      }
    }

    const appliedFromMarketFilters =
      hasContainerFilterApplied ||
      hasAbvFilterApplied ||
      hasDietaryFilterApplied;
    setFiltersAppliedFromMarket(appliedFromMarketFilters);
  };

  const navigateToStorefront = () => {
    if (source === "market") {
      setPreDefinedStorefrontFilterFromMarket();
    }
    navigateToChild(
      `${ORDER_BUYER.href({ supplierIdOrUrlId: supplier.urlId })}?categoryId=${redirectProductId}`,
    );
  };

  const moreFromButton = (className?: string) => {
    return (
      <Button
        className={twMerge("w-full", className)}
        onClick={navigateToStorefront}
      >
        <span>more from {supplierName}</span>
      </Button>
    );
  };
  if (!user || !business) {
    return (
      <div className={"flex w-full flex-col gap-2 md:grid md:grid-cols-2"}>
        <Button
          color={"secondary"}
          className={"col-span-1 w-full"}
          onClick={() => setSignupDialogActive(true)}
        >
          Sign up to order
        </Button>
        {moreFromButton("col-span-1")}
      </div>
    );
  }

  if (product.ProductPriceTier) {
    return (
      <Button className={"w-full"} onClick={navigateToStorefront}>
        <span>Continue to {supplierName}'s store</span>
      </Button>
    );
  }

  const hasConnectionWithDifferentPricing =
    !product.ProductPriceTier && !!connection;

  if (hasConnectionWithDifferentPricing) {
    return moreFromButton();
  }

  return (
    <>
      <div
        className={"flex w-full flex-col-reverse gap-2 md:grid md:grid-cols-2"}
      >
        {moreFromButton("col-span-1")}
      </div>
    </>
  );
};
